import React, { useState, useRef, useEffect } from 'react';
import { Plus, Trash2, Scale, Clipboard, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { TabularNumber } from '@/components/common/TabularNumber';
import { ScaleItemInput } from '@/types/database';

interface ScaleWeightGridProps {
  items: ScaleItemInput[];
  onChange: (items: ScaleItemInput[]) => void;
  shrinkageRateApplied: number;
  defaultPricePerKg?: number;
  disabled?: boolean;
}

export const ScaleWeightGrid: React.FC<ScaleWeightGridProps> = ({
  items,
  onChange,
  shrinkageRateApplied,
  defaultPricePerKg = 135.0,
  disabled = false,
}) => {
  const [pasteModalOpen, setPasteModalOpen] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [pasteError, setPasteError] = useState<string | null>(null);

  // Input refs for direct keyboard focus navigation on Gross Weight
  const inputRefs = useRef<Map<number, HTMLInputElement>>(new Map());

  // If items is empty initially, initialize with 1 blank row
  useEffect(() => {
    if (items.length === 0) {
      onChange([
        {
          sequence_no: 1,
          head_count: 1,
          gross_weight_kg: 0,
          price_per_kg: defaultPricePerKg,
          notes: '',
        },
      ]);
    }
  }, []);

  const handleAddRow = (focusNew: boolean = true) => {
    const nextSeq = items.length > 0 ? Math.max(...items.map((i) => i.sequence_no)) + 1 : 1;
    const lastPrice = items.length > 0 && items[items.length - 1].price_per_kg ? items[items.length - 1].price_per_kg : defaultPricePerKg;
    const newItems = [
      ...items,
      {
        sequence_no: nextSeq,
        head_count: 1,
        gross_weight_kg: 0,
        price_per_kg: lastPrice,
        notes: '',
      },
    ];
    onChange(newItems);

    if (focusNew) {
      setTimeout(() => {
        const nextInput = inputRefs.current.get(nextSeq);
        if (nextInput) {
          nextInput.focus();
          nextInput.select();
        }
      }, 30);
    }
  };

  const handleRemoveRow = (index: number) => {
    if (items.length <= 1) {
      // Keep at least one empty row
      onChange([
        {
          sequence_no: 1,
          head_count: 1,
          gross_weight_kg: 0,
          price_per_kg: defaultPricePerKg,
          notes: '',
        },
      ]);
      return;
    }
    const updated = items.filter((_, idx) => idx !== index);
    // Re-index sequences cleanly
    const reindexed = updated.map((it, idx) => ({ ...it, sequence_no: idx + 1 }));
    onChange(reindexed);
  };

  const handleUpdateField = (index: number, field: keyof ScaleItemInput, value: any) => {
    const updated = [...items];
    updated[index] = {
      ...updated[index],
      [field]: value,
    };
    onChange(updated);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === 'Enter') {
      // If we are at the last row and valid weight is entered, add a new row
      if (index === items.length - 1) {
        const currentVal = Number(items[index].gross_weight_kg);
        if (currentVal > 0) {
          e.preventDefault();
          handleAddRow(true);
        }
      } else {
        // Move to next row
        e.preventDefault();
        const nextSeq = items[index + 1].sequence_no;
        const nextInput = inputRefs.current.get(nextSeq);
        if (nextInput) {
          nextInput.focus();
          nextInput.select();
        }
      }
    } else if (e.key === 'ArrowDown') {
      if (index < items.length - 1) {
        e.preventDefault();
        const nextSeq = items[index + 1].sequence_no;
        const nextInput = inputRefs.current.get(nextSeq);
        if (nextInput) {
          nextInput.focus();
          nextInput.select();
        }
      }
    } else if (e.key === 'ArrowUp') {
      if (index > 0) {
        e.preventDefault();
        const prevSeq = items[index - 1].sequence_no;
        const prevInput = inputRefs.current.get(prevSeq);
        if (prevInput) {
          prevInput.focus();
          prevInput.select();
        }
      }
    }
  };

  const handleParsePaste = () => {
    setPasteError(null);
    if (!pasteText.trim()) {
      setPasteError('Please enter or paste weight numbers.');
      return;
    }

    // Split by commas, tabs, spaces, newlines
    const rawTokens = pasteText.split(/[\s,;\n\r\t]+/);
    const parsedWeights: number[] = [];

    for (const token of rawTokens) {
      const clean = token.trim();
      if (!clean) continue;
      const num = parseFloat(clean);
      if (!isNaN(num) && num > 0) {
        parsedWeights.push(Number(num.toFixed(2)));
      }
    }

    if (parsedWeights.length === 0) {
      setPasteError('No valid positive numbers found in pasted text.');
      return;
    }

    const newRows: ScaleItemInput[] = parsedWeights.map((wt, idx) => ({
      sequence_no: idx + 1,
      head_count: 1,
      gross_weight_kg: wt,
      price_per_kg: defaultPricePerKg,
      notes: '',
    }));

    onChange(newRows);
    setPasteModalOpen(false);
    setPasteText('');
  };

  // Calculations per line and overall
  const shrinkRate = Number(shrinkageRateApplied) || 0;
  const validItems = items.filter((it) => Number(it.gross_weight_kg) > 0);

  const totalHead = validItems.reduce((sum, it) => sum + Math.max(1, parseInt(String(it.head_count || 1), 10) || 1), 0);
  const totalGrossKg = Number(validItems.reduce((sum, it) => sum + (Number(it.gross_weight_kg) || 0), 0).toFixed(2));
  
  const totalShrinkKg = Number(
    validItems
      .reduce((sum, it) => {
        const qty = Math.max(1, parseInt(String(it.head_count || 1), 10) || 1);
        return sum + shrinkRate * qty;
      }, 0)
      .toFixed(2)
  );

  const totalNetKg = Number(
    validItems
      .reduce((sum, it) => {
        const qty = Math.max(1, parseInt(String(it.head_count || 1), 10) || 1);
        const gross = Number(it.gross_weight_kg) || 0;
        const shrink = shrinkRate * qty;
        return sum + Math.max(0, gross - shrink);
      }, 0)
      .toFixed(2)
  );

  const totalRevenue = Number(
    validItems
      .reduce((sum, it) => {
        const qty = Math.max(1, parseInt(String(it.head_count || 1), 10) || 1);
        const gross = Number(it.gross_weight_kg) || 0;
        const shrink = shrinkRate * qty;
        const net = Math.max(0, gross - shrink);
        const price = Number(it.price_per_kg ?? defaultPricePerKg ?? 0);
        return sum + net * price;
      }, 0)
      .toFixed(2)
  );

  const avgNetPerHead = totalHead > 0 ? totalNetKg / totalHead : 0;

  return (
    <div className="space-y-3">
      {/* Header with Quick Actions */}
      <div className="flex flex-wrap items-center justify-between gap-2 pb-1 border-b border-slate-200">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-md bg-emerald-100 text-emerald-800 flex items-center justify-center">
            <Scale className="w-4 h-4" />
          </div>
          <div>
            <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider">
              Scale Chute Weight Logger ({totalHead} {totalHead === 1 ? 'Pig' : 'Pigs'} Weighed)
            </h4>
            <p className="text-[11px] text-slate-500">
              Type gross weight and press <kbd className="px-1 py-0.5 bg-slate-100 border border-slate-300 rounded text-[10px] font-mono">Enter</kbd> to add next line automatically.
            </p>
          </div>
        </div>

        {!disabled && (
          <div className="flex items-center gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setPasteModalOpen(true)}
              className="h-7 text-xs px-2.5 text-slate-700 border-slate-300 hover:bg-slate-50"
            >
              <Clipboard className="w-3.5 h-3.5 mr-1 text-slate-500" /> Paste Weights
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={() => handleAddRow(true)}
              className="h-7 text-xs px-2.5 bg-emerald-700 hover:bg-emerald-600 font-medium text-white"
            >
              <Plus className="w-3.5 h-3.5 mr-1" /> Add Line (Enter)
            </Button>
          </div>
        )}
      </div>

      {/* Chute Weight Table */}
      <div className="border border-slate-200 rounded-lg overflow-hidden bg-white shadow-2xs">
        <div className="max-h-72 overflow-y-auto no-scrollbar">
          <table className="w-full text-xs text-left border-collapse">
            <thead className="bg-slate-100 text-slate-700 font-semibold border-b border-slate-200 sticky top-0 z-10">
              <tr>
                <th className="py-2 px-2.5 w-10 text-center">#</th>
                <th className="py-2 px-2 w-16 text-center">Quantity</th>
                <th className="py-2 px-2.5 w-28 text-right">Gross Wt (kg) *</th>
                <th className="py-2 px-2 w-20 text-right text-amber-900 bg-amber-50/50">Shrink (kg)</th>
                <th className="py-2 px-2.5 w-24 text-right text-emerald-900 bg-emerald-50/50">Net Wt (kg)</th>
                <th className="py-2 px-2.5 w-28 text-right">Base Price (₱/kg) *</th>
                <th className="py-2 px-2.5 w-28 text-right">Line Total (₱)</th>
                <th className="py-2 px-2.5">Notes</th>
                {!disabled && <th className="py-2 px-2 w-10 text-center"></th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((item, idx) => {
                const qty = Math.max(1, parseInt(String(item.head_count || 1), 10) || 1);
                const gross = Number(item.gross_weight_kg) || 0;
                const shrink = gross > 0 ? Number((shrinkRate * qty).toFixed(2)) : 0;
                const net = gross > 0 ? Number(Math.max(0, gross - shrink).toFixed(2)) : 0;
                const linePrice = item.price_per_kg !== undefined && item.price_per_kg !== null ? item.price_per_kg : defaultPricePerKg;
                const lineAmount = gross > 0 ? Number((net * Number(linePrice || 0)).toFixed(2)) : 0;

                return (
                  <tr key={item.sequence_no} className="hover:bg-slate-50/70 transition-colors">
                    <td className="py-1.5 px-2.5 text-center font-mono font-bold text-slate-500">
                      {item.sequence_no}
                    </td>
                    <td className="py-1 px-1.5 text-center">
                      <Input
                        type="number"
                        min="1"
                        step="1"
                        value={item.head_count === undefined || item.head_count === null ? 1 : item.head_count}
                        onChange={(e) => {
                          const val = e.target.value === '' ? 1 : Math.max(1, parseInt(e.target.value, 10) || 1);
                          handleUpdateField(idx, 'head_count', val);
                        }}
                        disabled={disabled}
                        className="h-8 text-center font-mono font-bold text-slate-900 text-xs px-1 w-14 mx-auto focus:ring-emerald-500 focus:border-emerald-500"
                      />
                    </td>
                    <td className="py-1 px-2 text-right">
                      <Input
                        ref={(el) => {
                          if (el) inputRefs.current.set(item.sequence_no, el);
                          else inputRefs.current.delete(item.sequence_no);
                        }}
                        type="number"
                        step="0.1"
                        min="0"
                        placeholder="0.0"
                        value={item.gross_weight_kg === 0 ? '' : item.gross_weight_kg}
                        onChange={(e) =>
                          handleUpdateField(idx, 'gross_weight_kg', e.target.value === '' ? 0 : parseFloat(e.target.value))
                        }
                        onKeyDown={(e) => handleKeyDown(e, idx)}
                        disabled={disabled}
                        className="h-8 text-right font-mono font-bold text-slate-900 text-xs px-2 focus:ring-emerald-500 focus:border-emerald-500"
                      />
                    </td>
                    <td className="py-1.5 px-2 text-right font-mono text-amber-800 bg-amber-50/30">
                      <TabularNumber value={shrink} decimals={2} />
                    </td>
                    <td className="py-1.5 px-2.5 text-right font-mono font-bold text-emerald-800 bg-emerald-50/30">
                      <TabularNumber value={net} decimals={2} />
                    </td>
                    <td className="py-1 px-2 text-right">
                      <Input
                        type="number"
                        step="0.5"
                        min="0"
                        placeholder="135.00"
                        value={item.price_per_kg === undefined || item.price_per_kg === null ? '' : item.price_per_kg}
                        onChange={(e) =>
                          handleUpdateField(idx, 'price_per_kg', e.target.value === '' ? '' : parseFloat(e.target.value))
                        }
                        disabled={disabled}
                        className="h-8 text-right font-mono text-slate-900 text-xs px-2 focus:ring-emerald-500 focus:border-emerald-500"
                      />
                    </td>
                    <td className="py-1.5 px-2.5 text-right font-mono font-medium text-slate-700">
                      {lineAmount > 0 ? <TabularNumber value={lineAmount} type="currency" decimals={2} /> : '₱0.00'}
                    </td>
                    <td className="py-1 px-2">
                      <Input
                        type="text"
                        placeholder="Optional remarks"
                        value={item.notes || ''}
                        onChange={(e) => handleUpdateField(idx, 'notes', e.target.value)}
                        disabled={disabled}
                        className="h-8 text-xs px-2 text-slate-600"
                      />
                    </td>
                    {!disabled && (
                      <td className="py-1 px-2 text-center">
                        <button
                          type="button"
                          tabIndex={-1}
                          onClick={() => handleRemoveRow(idx)}
                          className="p-1 text-slate-400 hover:text-red-600 rounded hover:bg-red-50 transition-colors"
                          title="Remove line"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Real-time Summary Footer */}
        <div className="p-3 bg-slate-900 text-white flex flex-wrap items-center justify-between gap-4 text-xs">
          <div className="flex items-center gap-4">
            <div>
              <span className="text-slate-400 text-[11px] block uppercase tracking-wider font-medium">Quantity</span>
              <span className="text-sm font-bold font-mono text-emerald-400">{totalHead} head</span>
            </div>
            <div className="border-l border-slate-700 pl-4">
              <span className="text-slate-400 text-[11px] block uppercase tracking-wider font-medium">Gross Weight</span>
              <span className="text-sm font-bold font-mono text-slate-100">
                <TabularNumber value={totalGrossKg} decimals={2} /> kg
              </span>
            </div>
            <div className="border-l border-slate-700 pl-4">
              <span className="text-slate-400 text-[11px] block uppercase tracking-wider font-medium">Shrinkage Deduction</span>
              <span className="text-sm font-bold font-mono text-amber-300">
                -<TabularNumber value={totalShrinkKg} decimals={2} /> kg
              </span>
            </div>
            <div className="border-l border-slate-700 pl-4">
              <span className="text-slate-400 text-[11px] block uppercase tracking-wider font-medium">Net Sale Weight</span>
              <span className="text-sm font-bold font-mono text-emerald-300">
                <TabularNumber value={totalNetKg} decimals={2} /> kg
              </span>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className="text-right">
              <span className="text-slate-400 text-[11px] block uppercase tracking-wider font-medium">Avg Net Wt</span>
              <span className="text-xs font-mono text-slate-300">
                <TabularNumber value={avgNetPerHead} decimals={2} /> kg/hd
              </span>
            </div>
            <div className="text-right border-l border-slate-700 pl-4">
              <span className="text-slate-400 text-[11px] block uppercase tracking-wider font-medium">Total Sale Revenue</span>
              <span className="text-base font-bold font-mono text-emerald-400">
                <TabularNumber value={totalRevenue} type="currency" decimals={2} />
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Paste Scale Weights Modal */}
      <Dialog open={pasteModalOpen} onOpenChange={setPasteModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base font-bold text-slate-900 flex items-center gap-2">
              <Clipboard className="w-4 h-4 text-emerald-700" /> Paste Scale Weights Batch
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2 text-xs">
            <p className="text-slate-600">
              Paste numeric scale weights from your clipboard, digital indicator output, or spreadsheet. Values can be separated by spaces, commas, or new lines.
            </p>
            {pasteError && (
              <div className="p-2.5 rounded bg-red-50 border border-red-200 text-red-700 flex items-center gap-1.5">
                <AlertCircle className="w-4 h-4 shrink-0" />
                <span>{pasteError}</span>
              </div>
            )}
            <textarea
              rows={6}
              value={pasteText}
              onChange={(e) => setPasteText(e.target.value)}
              placeholder="e.g. 104.5, 98.0, 112.5, 101.0, 99.5..."
              className="w-full p-2.5 font-mono text-xs border border-slate-300 rounded-md focus:ring-2 focus:ring-emerald-600 focus:outline-none"
            />
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => setPasteModalOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={handleParsePaste}
              className="bg-emerald-700 hover:bg-emerald-600 text-white font-medium"
            >
              Parse & Populate Grid
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
